/**
 * Tooltip content builders for abilities and defend action.
 * Generates HTML from ability config + live caster stats.
 */

import type { AbilityConfig, ServerCombatantState, ServerStatusEffect, StatusEffectTemplate, AffixInstance, AffixDef, PartyUnit, ConfigData } from '../../types';

import { displayMomentum } from '../../utils/format';
import { StateManager } from '../../managers/StateManager';
import { formatAffixValue } from './AffixDisplay';
import {
  elementClass, formatElement,
  formatTagList, formatCategory, formatRace, formatUnitClass,
  formatAbilityMechanics, formatStatName, getBarrierBuffEffect,
  formatStatusEffectMechanic, deriveBuffSubcategoryTags,
  formatAbilityDamageLine,
} from './GameTerms';

/** Get tier label for an explicit affix (e.g. "T1", "T3"). Returns empty string for implicits. */
export function getTierLabel(def: AffixDef, tierIndex: number): string {
  if (def.type === 'implicit' || !def.tiers || def.tiers.length <= 1) return '';
  return ` T${def.tiers.length - (tierIndex ?? 0)}`;
}

/**
 * Resolve an item's effective base definition. For uniques, merges the unique's
 * overrides (masteryRequirements, implicit) onto the real base. Mirrors the
 * backend `resolveItemBase` so tooltips see uniques as fully-formed bases.
 */
function resolveBaseDef(baseType: string, configData: ConfigData | undefined) {
  const uniqueDef = configData?.uniqueBases?.[baseType];
  const rawBase = uniqueDef
    ? configData?.itemBases?.[uniqueDef.baseId]
    : configData?.itemBases?.[baseType];
  if (uniqueDef && rawBase) {
    return {
      ...rawBase,
      masteryRequirements: uniqueDef.masteryRequirements ?? rawBase.masteryRequirements,
      implicitAffixId: uniqueDef.implicitAffixId ?? rawBase.implicitAffixId,
    };
  }
  return rawBase;
}

export { formatStatName } from './GameTerms';

/**
 * Stat display priority — lower = shown first. Prefixes before suffixes,
 * then within each group: HP > Focus > damage > crit > sustain > res > pen > misc.
 */
const STAT_SORT_PRIORITY: Record<string, number> = {
  // Core stats (prefixes typically)
  hp: 1,
  focus: 2,
  spd: 3,
  // Damage
  increased_damage: 10,
  flat_damage: 11, // category-level, catches all flat_damage
  // Crit
  crit_chance: 20,
  increased_crit_chance: 21,
  crit_multi: 22,
  // Sustain
  life_on_hit: 30,
  focus_on_hit: 31,
  hp_regen: 32,
  focus_on_hp_regen: 32.5,
  focus_regen: 33,
  focus_on_crit: 34,
  focus_on_kill: 35,
  // Healing
  healing_done: 40,
  healing_received: 41,
  // DoT
  dot_potency: 50,
  dot_multiplier: 51,
  flat_dot_damage: 52,
  // Status
  status_resistance: 61,
  dot_damage_taken: 62,
  chance_inflict_exposed: 63,
  // Lifesteal grouped with sustain
  lifesteal: 73,
};

/** Sort affixes for display: prefixes first, then suffixes. Stable order within each group by stat priority. */
export function sortAffixesForDisplay(affixes: AffixInstance[], configData: ConfigData | undefined): AffixInstance[] {
  return [...affixes].sort((a, b) => {
    const defA = configData?.affixes?.[a.affixId];
    const defB = configData?.affixes?.[b.affixId];
    // Prefixes before suffixes
    const typeA = defA?.type === 'prefix' ? 0 : 1;
    const typeB = defB?.type === 'prefix' ? 0 : 1;
    if (typeA !== typeB) return typeA - typeB;
    // Within same type, sort by stat priority
    const prioA = STAT_SORT_PRIORITY[defA?.stat ?? ''] ?? 100;
    const prioB = STAT_SORT_PRIORITY[defB?.stat ?? ''] ?? 100;
    return prioA - prioB;
  });
}

/** Format stat name for display — short label for stat breakdowns */
export function formatStatNameFromDef(def: AffixDef, configData: ConfigData | undefined): string {
  if (def.category === 'mastery') {
    return configData?.masteries?.[def.masteryId!]?.name ?? def.masteryId!.toUpperCase();
  }
  return formatStatName(def.stat);
}

/** Format an affix line — delegates value text to shared formatAffixValue, wraps in tooltip HTML */
function formatAffixLine(inst: AffixInstance, def: AffixDef, configData: ConfigData | undefined, valueClass: string, tierLabel: string, hideLabel = false): string {
  const valueText = formatAffixValue(inst, def, configData);
  const label = hideLabel ? '' : ` <span class="label">(${def.name}${tierLabel})</span>`;
  return `<div class="tooltip-line"><span class="value ${valueClass}">${valueText}</span>${label}</div>`;
}

// elementClass imported from GameTerms

/** Estimate Break Power for player combatants: `(rollRange + flatBP) × (1 + inc/100) × (1 + more/100)`. */
function estimatePlayerBreakPower(caster: ServerCombatantState, abilityId: string): [number, number] | null {
  const skill = caster.skills?.find(g => g.abilityId === abilityId);
  if (!skill) return null;
  const min = skill.breakPowerMin ?? 0;
  const max = skill.breakPowerMax ?? 0;
  const flatBP = (skill.flatBreakPower ?? 0) + (caster.flatBreakPower ?? 0);
  if (max <= 0 && flatBP <= 0) return null;
  const inc = caster.increasedBreakPower ?? 0;
  const more = caster.moreBreakPower ?? 0;
  const mult = (1 + inc / 100) * (1 + more / 100);
  const lo = Math.max(0, Math.floor((min + flatBP) * mult));
  const hi = Math.max(0, Math.floor((max + flatBP) * mult));
  return [lo, hi];
}

/** Estimate damage for player combatants using skill data + gear + mastery */
function estimatePlayerDamage(caster: ServerCombatantState, abilityId: string): [number, number] | null {
  const skill = caster.skills?.find(g => g.abilityId === abilityId);
  if (!skill) return null;

  const effectiveness = skill.damageEffectiveness;

  // Primary component: skill base range + matching flat damage from gear
  const flatKey = skill.element;
  const flatRange = caster.flatAddedDamage?.[flatKey] ?? { min: 0, max: 0 };
  const totalMin = skill.minDamage + flatRange.min * effectiveness;
  const totalMax = skill.maxDamage + flatRange.max * effectiveness;

  // Collect increased% sources: skill affixes + gear + mastery rank
  let increasedPct = skill.increasedPercent + (caster.equipIncreasedDamage ?? 0);
  if (skill.masteryId && caster.masteryRanks?.[skill.masteryId]) {
    increasedPct += caster.masteryRanks[skill.masteryId]; // 1% per rank
  }

  const lo = Math.max(1, Math.floor(totalMin * (1 + increasedPct / 100)));
  const hi = Math.max(1, Math.floor(totalMax * (1 + increasedPct / 100)));
  return [lo, hi];
}

/** Estimate heal for player combatants using skill data.
 *  Heal pipeline (matches `resolveHeal` + `scaleHeal` in src/services/combat/effects.ts):
 *    base = skillBase + (flatHealAmount × healEffectiveness)
 *    scaled = base × (1 + (healingDone + healingReceived) / 100) × (1 + healMore / 100)
 *  Heals do NOT use damageEffectiveness, flatAddedDamage (per-element), or
 *  equipIncreasedDamage — those are the damage pipeline. */
function estimatePlayerHeal(caster: ServerCombatantState, abilityId: string): [number, number] | null {
  const skill = caster.skills?.find(g => g.abilityId === abilityId);
  if (!skill) return null;

  const healEff = skill.healEffectiveness ?? 1.0;
  const flatHeal = caster.flatHealAmount ?? 0;
  const flatBonus = Math.floor(flatHeal * healEff);
  const totalMin = skill.minDamage + flatBonus;
  const totalMax = skill.maxDamage + flatBonus;

  const additivePct = (caster.healingDone ?? 0) + (caster.healingReceived ?? 0);
  const morePct = caster.healMore ?? 0;
  const scaledMin = Math.floor(totalMin * (1 + additivePct / 100) * (1 + morePct / 100));
  const scaledMax = Math.floor(totalMax * (1 + additivePct / 100) * (1 + morePct / 100));

  return [Math.max(1, scaledMin), Math.max(1, scaledMax)];
}

// (Removed local buildEffectLines — in-combat tooltips now route through
// formatAbilityMechanics + the EFFECT_DISPLAY_TEMPLATES registry in GameTerms.ts
// so wording stays consistent across shop, codex, skill tooltip, and in-combat.)

export function buildAbilityTooltip(
  ability: AbilityConfig,
  caster: ServerCombatantState,
  statusEffects: Record<string, StatusEffectTemplate>,
): string {
  const lines: string[] = [];

  // Header
  lines.push(`<div class="tooltip-inner">`);
  lines.push(`<div class="tooltip-header">`);
  lines.push(`<span class="tooltip-name">${ability.name}</span>`);
  lines.push(`<span class="tooltip-element ${elementClass(ability.element)}">${ability.element}</span>`);
  lines.push(`</div>`);

  // Tags — restored in combat to match codex/shop ("Spell · Aoe · Fire").
  const derivedTags = deriveBuffSubcategoryTags(ability, statusEffects);
  const allTags = [...(ability.tags ?? []), ...derivedTags];
  const tagStr = formatTagList(allTags);
  if (tagStr) {
    lines.push(`<div class="tooltip-line"><span class="label">${tagStr.replace(/, /g, ' · ')}</span></div>`);
  }

  // ── Cost / stat block (above the separator) ──
  // Focus + Momentum on one row (paired), Crit on its own, Cast Time on its own.
  const skillData = caster.side === 'player' ? caster.skills?.find(g => g.abilityId === ability.id) : undefined;
  const fCost = skillData?.focusCost ?? (ability as unknown as Record<string, unknown>).focusCost as number ?? 0;
  const mCost = skillData?.momentumCost ?? ability.momentumCost ?? 10000;
  const costClass = mCost < 10000 ? 'buff' : mCost > 10000 ? 'debuff' : '';
  const costParts: string[] = [];
  if (fCost > 0) {
    costParts.push(`<span class="label">Focus:</span> <span class="value">${fCost}</span>`);
  } else if (fCost < 0) {
    costParts.push(`<span class="label">Focus Restore:</span> <span class="value">+${Math.abs(fCost)}</span>`);
  }
  costParts.push(`<span class="label">Momentum:</span> <span class="value ${costClass}">${displayMomentum(mCost)}</span>`);
  lines.push(`<div class="tooltip-line">${costParts.join(' · ')}</div>`);

  // Crit chance: (ability base + flat gear crit) × (1 + increased gear crit / 100)
  if (caster.side === 'player') {
    const baseCrit = (skillData?.critChance ?? 0) + (caster.equipCritChance ?? 0);
    const totalCrit = baseCrit * (1 + (caster.equipIncreasedCritChance ?? 0) / 100);
    if (totalCrit > 0) {
      lines.push(`<div class="tooltip-line"><span class="label">Crit Chance:</span> <span class="value">${Math.round(totalCrit)}%</span></div>`);
    }
  }

  // Cast time — charged abilities take N turns to charge before firing.
  if (ability.castTurns && ability.castTurns > 0) {
    lines.push(`<div class="tooltip-line"><span class="label">Cast Time:</span> <span class="value debuff">${ability.castTurns} turn${ability.castTurns > 1 ? 's' : ''}</span></div>`);
  }

  // Cooldown — paired conceptually with Cast Time (both are timing constraints).
  // The live "Ready in" line sits above the static "Cooldown" so the actionable
  // info reads first when the ability is currently locked.
  const cdRemaining = caster.abilityCooldowns?.[ability.id] ?? 0;
  if (cdRemaining > 0) {
    lines.push(`<div class="tooltip-line"><span class="label">Ready in:</span> <span class="value debuff">${cdRemaining} turn${cdRemaining > 1 ? 's' : ''}</span></div>`);
  }
  const cdBase = skillData?.cooldown ?? ability.cooldown;
  if (cdBase && cdBase > 0) {
    lines.push(`<div class="tooltip-line"><span class="label">Cooldown:</span> <span class="value debuff">${cdBase} turn${cdBase > 1 ? 's' : ''}</span></div>`);
  }

  lines.push(`<div class="tooltip-separator"></div>`);

  // ── Effect block (below the separator) ──
  // Damage/heal as a prose line including the target ("Deals 15-63 Fire damage to all enemies").
  // Summons skip — the player cares about the minion's kit, not the cast itself.
  if (ability.type !== 'summon') {
    let dmgMin: number | undefined;
    let dmgMax: number | undefined;
    if (ability.type === 'damage' || ability.type === 'trap') {
      if (caster.side === 'player') {
        const est = estimatePlayerDamage(caster, ability.id);
        if (est) { dmgMin = est[0]; dmgMax = est[1]; }
      } else if (ability.minDamage != null && ability.maxDamage != null) {
        dmgMin = ability.minDamage; dmgMax = ability.maxDamage;
      }
    } else if (ability.type === 'heal') {
      if (caster.side === 'player') {
        const est = estimatePlayerHeal(caster, ability.id);
        if (est) { dmgMin = est[0]; dmgMax = est[1]; }
      } else if (ability.minDamage != null && ability.maxDamage != null) {
        dmgMin = ability.minDamage; dmgMax = ability.maxDamage;
      }
    }
    if (dmgMin != null && dmgMax != null) {
      lines.push(`<div class="tooltip-line">${formatAbilityDamageLine({
        type: ability.type as 'damage' | 'heal' | 'trap',
        element: ability.element,
        target: ability.target,
        min: dmgMin,
        max: dmgMax,
      })}</div>`);
    }
  }

  // Break Power — grouped with effects, since it's a property of the cast.
  if (caster.side === 'player') {
    const bp = estimatePlayerBreakPower(caster, ability.id);
    if (bp) {
      const [lo, hi] = bp;
      lines.push(`<div class="tooltip-line"><span class="label">Builds:</span> <span class="value">${lo}-${hi} Break Power</span></div>`);
    }
  } else if (ability.breakPowerMin != null && ability.breakPowerMax != null && ability.breakPowerMax > 0) {
    lines.push(`<div class="tooltip-line"><span class="label">Builds:</span> <span class="value">${ability.breakPowerMin}-${ability.breakPowerMax} Break Power</span></div>`);
  }

  // ── Mechanics (effects, tempo, lifesteal, etc.) ──
  // Route through the prescribed registry so in-combat wording matches shop/codex/inventory.
  // Pull per-rank potency + barrier amount from the caster's runtime skill data.
  const inCombatBarrierEffect = getBarrierBuffEffect(ability, { statusEffects });
  const inCombatHasPoised = ability.applySelfEffect === 'poised';
  const inCombatTrapEffect = ability.applyTargetEffect
    ? statusEffects[ability.applyTargetEffect]?.trapTriggerCondition
    : undefined;
  const inCombatHasManifestBlade = ability.applySelfEffect === 'manifest_blade';
  const inCombatHasBristle = ability.applySelfEffect === 'bristle';
  const inCombatHasBarrierRider = !!ability.appliesSelfBarrierEffect;
  const inCombatMechCtx = skillData
    ? {
        barrierRange: inCombatBarrierEffect ? { min: skillData.minDamage, max: skillData.maxDamage } : undefined,
        barrierRiderRange: inCombatHasBarrierRider && skillData.barrierMin != null && skillData.barrierMax != null
          ? { min: skillData.barrierMin, max: skillData.barrierMax }
          : undefined,
        counterDamageRange: inCombatHasPoised ? { min: skillData.minDamage, max: skillData.maxDamage } : undefined,
        trapDamageRange: inCombatTrapEffect ? { min: skillData.minDamage, max: skillData.maxDamage } : undefined,
        infusionRange: inCombatHasManifestBlade ? { min: skillData.minDamage, max: skillData.maxDamage } : undefined,
        thornsRange: inCombatHasBristle ? { min: skillData.minDamage, max: skillData.maxDamage } : undefined,
        potency: skillData.effectPotency,
        dotDamage: skillData.dotDamage,
        regenAmount: skillData.regenPerTurn,
        chargesGranted: skillData.chargesGranted,
      }
    : undefined;
  // Pull full configData so summon abilities can resolve their minion template + ability lookups.
  const inCombatConfig = StateManager.get('configData') as ConfigData | undefined;
  const inCombatMechLines = formatAbilityMechanics(
    ability,
    {
      statusEffects,
      minionTemplates: inCombatConfig?.minionTemplates,
      abilities: inCombatConfig?.abilities,
    },
    inCombatMechCtx,
  );
  for (const line of inCombatMechLines) {
    lines.push(`<div class="tooltip-line">${line}</div>`);
  }

  lines.push(`</div>`);
  return lines.join('');
}

export function buildDefendTooltip(
  statusEffects: Record<string, StatusEffectTemplate>,
): string {
  const lines: string[] = [];

  lines.push(`<div class="tooltip-inner">`);
  lines.push(`<div class="tooltip-header">`);
  lines.push(`<span class="tooltip-name">Defend</span>`);
  lines.push(`<span class="tooltip-element element-physical">physical</span>`);
  lines.push(`</div>`);

  lines.push(`<div class="tooltip-line"><span class="label">Skip your attack to raise guard.</span></div>`);
  lines.push(`<div class="tooltip-line"><span class="label">Momentum Cost:</span> <span class="value">10000 (Standard)</span></div>`);
  lines.push(`<div class="tooltip-separator"></div>`);

  // Show martial_res_up details via the unified mechanic registry.
  // Defend is conceptually a self-targeted buff cast, so we synthesize a
  // minimal AbilityConfig and route through formatAbilityMechanics.
  const defendShim = {
    id: 'defend',
    type: 'buff',
    target: 'self',
    element: 'physical',
    multiplier: 0,
    momentumCost: 10000,
    focusCost: 0,
    masteryId: undefined,
    applySelfEffect: 'martial_res_up',
    tags: ['player', 'buff'],
  } as unknown as AbilityConfig;
  const defendLines = formatAbilityMechanics(defendShim, { statusEffects });
  for (const line of defendLines) {
    lines.push(`<div class="tooltip-line">${line}</div>`);
  }

  lines.push(`</div>`);
  return lines.join('');
}

export function buildStatusEffectTooltip(effect: ServerStatusEffect): string {
  const lines: string[] = [];
  const categoryClass = effect.category === 'buff' ? 'buff' : 'debuff';

  lines.push(`<div class="tooltip-inner">`);
  lines.push(`<div class="tooltip-header">`);
  lines.push(`<span class="tooltip-name">${effect.name}</span>`);
  lines.push(`<span class="tooltip-element ${categoryClass === 'buff' ? 'element-holy' : 'element-fire'}">${effect.category}</span>`);
  lines.push(`</div>`);

  // Mechanic body — single source of truth for status pill descriptions.
  const configData = StateManager.get('configData') as ConfigData | undefined;
  const template = configData?.statusEffects?.[effect.id];

  // Subcategory line (Hex / Ruin / Boon / Barrier / Regen) — shown above the
  // mechanic so the player can scan which gear bucket scales the effect.
  const sub = template?.subcategory ?? (template?.category === 'buff' ? 'boon' : undefined);
  if (sub === 'hex' || sub === 'ruin' || sub === 'boon' || sub === 'barrier' || sub === 'regen') {
    const subLabel = sub.charAt(0).toUpperCase() + sub.slice(1);
    lines.push(`<div class="tooltip-line"><span class="label">${subLabel}</span></div>`);
  }
  if (template) {
    const mechanicLines = formatStatusEffectMechanic(template, {
      multiplier: effect.multiplier,
      dotDamage: effect.dotDamage,
      regenAmount: effect.regenAmount,
      stackCount: effect.stackCount,
      potency: effect.potency,
      bonusCritMulti: effect.bonusCritMulti,
    });
    for (const line of mechanicLines) {
      lines.push(`<div class="tooltip-line">${line}</div>`);
    }
  }

  // Stack count for stackable effects — shown above duration so it's the first numeric line.
  if (effect.stackCount && effect.stackCount > 1) {
    lines.push(`<div class="tooltip-separator"></div>`);
    const max = template?.maxStacks;
    const stackText = max ? `${effect.stackCount} / ${max}` : String(effect.stackCount);
    lines.push(`<div class="tooltip-line"><span class="label">Stacks:</span> <span class="value">${stackText}</span></div>`);
  }

  if (effect.turnsRemaining > 0) {
    if (!(effect.stackCount && effect.stackCount > 1)) {
      lines.push(`<div class="tooltip-separator"></div>`);
    }
    lines.push(`<div class="tooltip-line"><span class="label">Remaining:</span> <span class="value">${effect.turnsRemaining} turn${effect.turnsRemaining !== 1 ? 's' : ''}</span></div>`);
  }

  lines.push(`</div>`);
  return lines.join('');
}

// ─── Rarity helpers ───

const RARITY_COLORS: Record<string, string> = {
  normal: '#cccccc',
  magic: '#6688ff',
  rare: '#ffff00',
  unique: '#af6025',
};

export function rarityColor(rarity: string): string {
  return RARITY_COLORS[rarity] ?? '#cccccc';
}

export function rarityEmoji(rarity: string): string {
  switch (rarity) {
    case 'rare': return '\u2B50';     // star
    case 'magic': return '\uD83D\uDD39'; // small blue diamond
    default: return '\u26AA';          // white circle
  }
}

// ─── Item tooltip ───

export function buildItemTooltip(item: import('../../types').ItemData, configData: ConfigData | undefined, unit?: PartyUnit | null): string {
  const lines: string[] = [];
  const rColor = rarityColor(item.rarity ?? 'normal');
  const rarityLabel = formatElement(item.rarity ?? 'normal');
  const baseDef = resolveBaseDef(item.baseType, configData);

  // Build category label with handedness/grip info
  let categoryLabel = formatCategory(item.category);
  if (baseDef?.category === 'weapon' && baseDef.handedness) {
    categoryLabel = `${baseDef.handedness === '2h' ? 'Two-Handed' : 'One-Handed'} ${categoryLabel}`;
  }

  lines.push(`<div class="tooltip-inner">`);
  lines.push(`<div class="tooltip-header">`);
  lines.push(`<span class="tooltip-name" style="color:${rColor}">${item.name}</span>`);
  lines.push(`</div>`);

  lines.push(`<div class="tooltip-line"><span class="label">${rarityLabel} ${categoryLabel}</span></div>`);

  // Combined requirements line: masteries + offhand mainhand coupling, comma-joined
  const reqParts: string[] = [];
  if (baseDef?.masteryRequirements && baseDef.masteryRequirements.length > 0) {
    for (const r of baseDef.masteryRequirements) {
      const name = configData?.masteries?.[r.masteryId]?.name ?? r.masteryId;
      const color = unit
        ? ((unit.computedStats.masteryRanks[r.masteryId] ?? 0) >= r.rank ? '#00ff88' : '#ff4444')
        : '#ff8844';
      reqParts.push(`<span style="color:${color}">${name} ${r.rank}</span>`);
    }
  }
  if (baseDef?.category === 'offhand' && baseDef.grip && baseDef.grip !== 'worn') {
    const items = (StateManager.get('itemData') as import('../../types').ItemData[] | undefined) ?? [];
    const weapon = unit?.weaponId ? items.find(i => i.id === unit.weaponId) : undefined;
    const weaponBase = weapon ? resolveBaseDef(weapon.baseType, configData) : undefined;

    let met = false;
    let reqText = '';
    if (baseDef.grip === 'quivered') {
      reqText = 'Bow equipped';
      met = weaponBase?.category === 'weapon' && weaponBase.family === 'bow';
    } else if (baseDef.grip === 'held') {
      reqText = 'One-Handed Weapon';
      met = !weaponBase || weaponBase.handedness !== '2h';
    }
    const color = unit ? (met ? '#00ff88' : '#ff4444') : '#ff8844';
    reqParts.push(`<span style="color:${color}">${reqText}</span>`);
  }
  if (reqParts.length > 0) {
    lines.push(`<div class="tooltip-line"><span class="label">Requires: ${reqParts.join(', ')}</span></div>`);
  }

  const isUnique = item.rarity === 'unique';

  // Implicit affix
  if (item.implicitAffix) {
    try {
      const implicit: AffixInstance = JSON.parse(item.implicitAffix);
      const def: AffixDef | undefined = configData?.affixes?.[implicit.affixId];
      if (def) {
        lines.push(`<div class="tooltip-separator"></div>`);
        lines.push(formatAffixLine(implicit, def, configData, 'implicit', '', isUnique));
      }
    } catch { /* ignore */ }
  }

  // Explicit affixes — sorted: prefixes first, then suffixes, stable order within each group
  if (item.explicitAffixes) {
    try {
      const affixes: AffixInstance[] = JSON.parse(item.explicitAffixes);
      if (affixes.length > 0) {
        const sorted = sortAffixesForDisplay(affixes, configData);
        lines.push(`<div class="tooltip-separator"></div>`);
        for (const inst of sorted) {
          const def: AffixDef | undefined = configData?.affixes?.[inst.affixId];
          if (def) {
            const valueClass = isUnique ? 'unique' : (def.type === 'prefix' ? 'buff' : 'heal');
            lines.push(formatAffixLine(inst, def, configData, valueClass, getTierLabel(def, inst.tier ?? 0), isUnique));
          }
        }
      }
    } catch { /* ignore */ }
  }

  lines.push(`</div>`);
  return lines.join('');
}

// ─── Unit tooltip ───

export function buildUnitTooltip(unit: PartyUnit, configData: ConfigData | undefined): string {
  const lines: string[] = [];
  const rColor = rarityColor(unit.rarity ?? 'normal');
  const rarityLabel = formatElement(unit.rarity ?? 'normal');
  const classLabel = formatUnitClass(unit.unitClass);
  const cs = unit.computedStats;

  lines.push(`<div class="tooltip-inner">`);
  lines.push(`<div class="tooltip-header">`);
  lines.push(`<span class="tooltip-name" style="color:${rColor}">${unit.name}</span>`);
  const dtypes = unit.defensiveTypes ?? [];
  if (dtypes.length > 0) {
    lines.push(dtypes.map(t => `<span class="tooltip-element ${elementClass(t)}">${t}</span>`).join(' / '));
  } else {
    lines.push(`<span class="tooltip-element ${elementClass(unit.element)}">${unit.element}</span>`);
  }
  lines.push(`</div>`);

  lines.push(`<div class="tooltip-line"><span class="label">${rarityLabel} ${classLabel}</span> · <span class="label">${formatRace(unit.race)}</span></div>`);
  lines.push(`<div class="tooltip-line"><span class="label">Level:</span> <span class="value">${unit.level ?? 1}</span>${(unit.unspentMasteryPoints ?? 0) > 0 ? ` · <span class="value" style="color:#d4a017">${unit.unspentMasteryPoints} MP</span>` : ''}</div>`);

  // Computed stats in header block
  lines.push(`<div class="tooltip-line"><span class="label">Health ${cs.hp}</span> · <span class="label">Speed ${cs.spd}</span></div>`);
  // Mastery ranks
  if (cs.masteryRanks) {
    const rankEntries = Object.entries(cs.masteryRanks).filter(([, v]) => v > 0);
    if (rankEntries.length > 0) {
      const masteryStr = rankEntries.map(([id, v]) => {
        const name = configData?.masteries?.[id]?.name ?? id;
        return `${name} ${v}`;
      }).join(' · ');
      lines.push(`<div class="tooltip-line"><span class="label">${masteryStr}</span></div>`);
    }
  }

  // Implicit affix
  if (unit.implicitAffix) {
    try {
      const implicit: AffixInstance = JSON.parse(unit.implicitAffix);
      const def: AffixDef | undefined = configData?.affixes?.[implicit.affixId];
      if (def) {
        lines.push(`<div class="tooltip-separator"></div>`);
        lines.push(formatAffixLine(implicit, def, configData, '', ''));
      }
    } catch { /* ignore */ }
  }

  // Explicit affixes — sorted: prefixes first, then suffixes
  if (unit.explicitAffixes) {
    try {
      const affixes: AffixInstance[] = JSON.parse(unit.explicitAffixes);
      if (affixes.length > 0) {
        const sorted = sortAffixesForDisplay(affixes, configData);
        lines.push(`<div class="tooltip-separator"></div>`);
        for (const inst of sorted) {
          const def: AffixDef | undefined = configData?.affixes?.[inst.affixId];
          if (def) {
            const valueClass = def.type === 'prefix' ? 'buff' : 'heal';
            lines.push(formatAffixLine(inst, def, configData, valueClass, getTierLabel(def, inst.tier ?? 0)));
          }
        }
      }
    } catch { /* ignore */ }
  }

  lines.push(`</div>`);
  return lines.join('');
}

// ─── Shared skill ability info block ───

export interface SkillRankInfo {
  minDamage: number;
  maxDamage: number;
  ilvlRequired?: number;
  masteryRequirements?: { masteryId: string; rank: number }[];
  effectPotency?: number;
  dotDamage?: number;
  /** Per-rank regen-per-turn (Tonic / nature_regen). */
  regenPerTurn?: number;
  /** Per-rank charge count for charge-grant buffs (Venomcoat). */
  chargesGranted?: number;
  /** Per-rank Break Power range (rolled per cast, independent of damage). */
  breakPowerMin?: number;
  breakPowerMax?: number;
  /** Player-summon skills: minion HP at this rank. */
  summonHp?: number;
  /** Player-summon skills: turns the minion lasts before automatic expiry. */
  summonDurationTurns?: number;
  /** Damage+barrier hybrid: per-rank barrier range (when ability has appliesSelfBarrierEffect). */
  barrierMin?: number;
  barrierMax?: number;
}

/**
 * Build the ability info block for a skill — used by tooltip, codex detail, shop, and codex cards.
 * Returns an array of HTML line strings using tooltip-* classes.
 *
 * PoE-style structure: tags → stat block (Focus/Momentum/Crit/Cast/Cooldown) → separator
 * → effect block (damage prose, break power, mechanics). Every surface that displays a skill
 * uses this layout — no compact variant. If a caller wants a shorter card, it should crop
 * which lines it renders, not invent a new ordering.
 *
 * Options:
 * - unit: if provided, color-code requirements as met/unmet
 * - skillEstimate: if provided, show computed damage instead of base
 * - skillId: needed to look up skillEstimate from unit.computedStats when skillEstimate not passed directly
 * - rankCount: total number of ranks (for codex detail display, e.g. "Rank 1–15")
 * - hideRequirements: if true, skip the "Requires: ..." line (caller is displaying it themselves)
 */
export function buildSkillAbilityLines(
  ability: AbilityConfig,
  rankData: SkillRankInfo | undefined,
  configData: ConfigData | undefined,
  options?: {
    unit?: PartyUnit | null;
    skillEstimate?: { minDamage: number; maxDamage: number; increasedPercent: number } | null;
    skillId?: string;
    rankCount?: number;
    hideRequirements?: boolean;
  },
): string[] {
  const lines: string[] = [];
  const unit = options?.unit;
  const skillId = options?.skillId;

  // Resolve skillEstimate: use passed value, or look up from unit's computedStats
  let skillEstimate = options?.skillEstimate;
  if (skillEstimate === undefined && unit && skillId) {
    const est = unit.computedStats?.skillEstimates?.find(e => e.skillId === skillId);
    if (est) skillEstimate = est;
  }

  // ── Requirements ──
  if (rankData && !options?.hideRequirements) {
    const reqParts: string[] = [];

    // Level requirement (legacy — skills no longer have ilvl gates)
    if (rankData.ilvlRequired && rankData.ilvlRequired > 1) {
      const levelMet = !unit || unit.level >= rankData.ilvlRequired;
      const levelColor = unit ? (levelMet ? '#00ff88' : '#ff4444') : '#ff8844';
      reqParts.push(`<span style="color:${levelColor}">Level ${rankData.ilvlRequired}</span>`);
    }

    // Mastery requirements — "R" prefix matches the codex/party convention so the
    // number always reads as a rank value, never a level or amount.
    if (rankData.masteryRequirements?.length) {
      for (const r of rankData.masteryRequirements) {
        const name = configData?.masteries?.[r.masteryId]?.name ?? r.masteryId;
        const color = unit
          ? ((unit.computedStats.masteryRanks[r.masteryId] ?? 0) >= r.rank ? '#00ff88' : '#ff4444')
          : '#ff8844';
        reqParts.push(`<span style="color:${color}">${name} R${r.rank}</span>`);
      }
    }

    if (reqParts.length > 0) {
      lines.push(`<div class="tooltip-line"><span class="label">Requires: ${reqParts.join(', ')}</span></div>`);
    }
  }

  // ── Tags (PoE-style: dot-separated, e.g. "Spell · Aoe · Fire") ──
  // Auto-append Boon / Barrier / Regen tags from applied buffs so players can scan
  // which abilities benefit from boon_potency / barrier_potency / healing_done.
  const derivedTags = deriveBuffSubcategoryTags(ability, configData?.statusEffects);
  const allTags = [...(ability.tags ?? []), ...derivedTags];
  const tagStr = formatTagList(allTags);
  if (tagStr) {
    lines.push(`<div class="tooltip-line"><span class="label">${tagStr.replace(/, /g, ' · ')}</span></div>`);
  }

  // ── Cost / stat block ──
  // Focus + Momentum on one row, Crit / Effectiveness / Cast Time each on their own.
  // Per-rank cost overrides (Archetype-3 cost-scale skills: Antidote, Wildfire Oil, Sirocco, etc.)
  // win over the AbilityConfig defaults. Without this branch the codex/tooltip prints a constant
  // cost for every rank even when the SkillRank ladder is a cost-down curve.
  const rankFocus = (rankData as { focusCost?: number } | undefined)?.focusCost;
  const rankMom = (rankData as { momentumCost?: number } | undefined)?.momentumCost;
  const effectiveFocus = rankFocus ?? ability.focusCost;
  const effectiveMom = rankMom ?? ability.momentumCost ?? 10000;
  const costParts: string[] = [];
  if (effectiveFocus) {
    if (effectiveFocus < 0) {
      costParts.push(`<span class="label">Focus Restore:</span> <span class="value">+${Math.abs(effectiveFocus)}</span>`);
    } else {
      costParts.push(`<span class="label">Focus:</span> <span class="value">${effectiveFocus}</span>`);
    }
  }
  const mCostClass = effectiveMom < 10000 ? 'buff' : effectiveMom > 10000 ? 'debuff' : '';
  costParts.push(`<span class="label">Momentum:</span> <span class="value ${mCostClass}">${displayMomentum(effectiveMom)}</span>`);
  lines.push(`<div class="tooltip-line">${costParts.join(' · ')}</div>`);

  // Critical strike chance
  if (ability.critChance && ability.critChance > 0) {
    lines.push(`<div class="tooltip-line"><span class="label">Crit Chance:</span> <span class="value">${ability.critChance}%</span></div>`);
  }

  // Effectiveness (only show if not default 1.0)
  // Damage abilities read `damageEffectiveness` (scales flat-added damage from gear).
  // Heal abilities read `healEffectiveness` (scales flatHealAmount). Buff/cleanse/summon
  // skip (no flat-gear lever applies). For HoT-shaped buffs that grant a regen status,
  // the eff scales the per-tick flat-heal contribution (Option B, 2026-05-21).
  const isHealLike = ability.type === 'heal' || (ability.type === 'buff' && ability.healEffectiveness !== undefined);
  const eff = isHealLike
    ? (ability.healEffectiveness ?? 1.0)
    : (ability.damageEffectiveness ?? 1.0);
  const isPctMaxHpHealForEff = ability.healPercentMaxHp !== undefined;
  const showEff = ability.type === 'damage' || ability.type === 'heal' || isHealLike;
  if (eff !== 1.0 && !isPctMaxHpHealForEff && showEff) {
    lines.push(`<div class="tooltip-line"><span class="label">Effectiveness:</span> <span class="value">${Math.round(eff * 100)}%</span></div>`);
  }

  // Cast time
  if (ability.castTurns && ability.castTurns > 0) {
    lines.push(`<div class="tooltip-line"><span class="label">Cast Time:</span> <span class="value debuff">${ability.castTurns} turn${ability.castTurns > 1 ? 's' : ''}</span></div>`);
  }

  // Cooldown — no live remaining in this path (codex/static), just the base lever.
  // Per-rank cooldown override on SkillRank wins over the ability default.
  const rankCd = (rankData as { cooldown?: number } | undefined)?.cooldown;
  const effectiveCd = rankCd ?? ability.cooldown;
  if (effectiveCd && effectiveCd > 0) {
    lines.push(`<div class="tooltip-line"><span class="label">Cooldown:</span> <span class="value debuff">${effectiveCd} turn${effectiveCd > 1 ? 's' : ''}</span></div>`);
  }

  lines.push(`<div class="tooltip-separator"></div>`);

  // ── Effect block ──
  // % Max HP heals (Shrug Off et al.) skip the damage-range and weapon-scaling
  // blocks entirely — the mechanic line is rendered below by formatAbilityMechanics.
  const isPctMaxHpHeal = ability.healPercentMaxHp !== undefined;

  // Damage/heal range as a prose line ("Deals 15-63 Fire damage to all enemies").
  // The damage/heal/break lines are the primary effect and render in the same white
  // prose as the mechanic lines below — the stat-block above the separator is the
  // grey-label zone, everything below the separator reads as the spell's actions.
  if (!isPctMaxHpHeal && rankData && (ability.type === 'damage' || ability.type === 'heal' || ability.type === 'trap') && (rankData.minDamage > 0 || rankData.maxDamage > 0)) {
    if (skillEstimate && (skillEstimate.minDamage > 0 || skillEstimate.maxDamage > 0)) {
      // Show computed damage (includes gear flat, mastery rank, increased%)
      const incPct = skillEstimate.increasedPercent;
      const estMin = Math.max(1, Math.floor(skillEstimate.minDamage * (1 + incPct / 100)));
      const estMax = Math.max(1, Math.floor(skillEstimate.maxDamage * (1 + incPct / 100)));
      lines.push(`<div class="tooltip-line tooltip-mechanic">${formatAbilityDamageLine({
        type: ability.type as 'damage' | 'heal' | 'trap',
        element: ability.element,
        target: ability.target,
        min: estMin,
        max: estMax,
      })}</div>`);
      lines.push(`<div class="tooltip-line"><span class="label" style="color:rgba(255,255,255,0.35)">Base: ${rankData.minDamage}–${rankData.maxDamage} · +${Math.round(incPct)}% increased</span></div>`);
    } else {
      // No unit context — show base rank data
      lines.push(`<div class="tooltip-line tooltip-mechanic">${formatAbilityDamageLine({
        type: ability.type as 'damage' | 'heal' | 'trap',
        element: ability.element,
        target: ability.target,
        min: rankData.minDamage,
        max: rankData.maxDamage,
      })}</div>`);
    }
  } else if (!isPctMaxHpHeal && rankData && ability.type === 'heal') {
    // Pure-scaling heal (rank base is 0/0) — heal = weapon flat element damage × Effectiveness.
    // Without a unit context the player can still see what the heal scales from.
    const elLabel = formatElement(ability.element);
    if (skillEstimate && (skillEstimate.minDamage > 0 || skillEstimate.maxDamage > 0)) {
      const incPct = skillEstimate.increasedPercent;
      const estMin = Math.max(1, Math.floor(skillEstimate.minDamage * (1 + incPct / 100)));
      const estMax = Math.max(1, Math.floor(skillEstimate.maxDamage * (1 + incPct / 100)));
      lines.push(`<div class="tooltip-line tooltip-mechanic">${formatAbilityDamageLine({
        type: 'heal',
        element: ability.element,
        target: ability.target,
        min: estMin,
        max: estMax,
      })}</div>`);
      lines.push(`<div class="tooltip-line"><span class="label" style="color:rgba(255,255,255,0.35)">Heals from weapon ${elLabel} Damage · +${Math.round(incPct)}% increased</span></div>`);
    } else {
      lines.push(`<div class="tooltip-line tooltip-mechanic">Heals based on weapon <span class="heal">${elLabel} Damage</span></div>`);
    }
  }

  // Break Power — per-rank range. Rolled independently of damage each cast.
  // PoE-style prose: "Builds X-Y Break Power" rather than a label:value stat row.
  if (rankData && (rankData.breakPowerMax ?? 0) > 0) {
    const bMin = rankData.breakPowerMin ?? 0;
    const bMax = rankData.breakPowerMax!;
    lines.push(`<div class="tooltip-line tooltip-mechanic">Builds <strong>${bMin}-${bMax}</strong> Break Power</div>`);
  } else if (!rankData && ability.breakPowerMin != null && ability.breakPowerMax != null && ability.breakPowerMax > 0) {
    lines.push(`<div class="tooltip-line tooltip-mechanic">Builds <strong>${ability.breakPowerMin}-${ability.breakPowerMax}</strong> Break Power</div>`);
  }

  // ── Mechanics (effects, tempo, lifesteal, etc.) ──
  const barrierEffect = getBarrierBuffEffect(ability, configData);
  const hasPoisedSelfEffect = ability.applySelfEffect === 'poised';
  const trapEffect = ability.applyTargetEffect && configData?.statusEffects
    ? configData.statusEffects[ability.applyTargetEffect]?.trapTriggerCondition
    : undefined;
  const hasManifestBlade = ability.applySelfEffect === 'manifest_blade';
  const hasBristleSelfEffect = ability.applySelfEffect === 'bristle';
  const hasBarrierRider = !!ability.appliesSelfBarrierEffect;
  const mechCtx = rankData
    ? {
        barrierRange: barrierEffect ? { min: rankData.minDamage, max: rankData.maxDamage } : undefined,
        barrierRiderRange: hasBarrierRider && rankData.barrierMin != null && rankData.barrierMax != null
          ? { min: rankData.barrierMin, max: rankData.barrierMax }
          : undefined,
        counterDamageRange: hasPoisedSelfEffect ? { min: rankData.minDamage, max: rankData.maxDamage } : undefined,
        trapDamageRange: trapEffect ? { min: rankData.minDamage, max: rankData.maxDamage } : undefined,
        infusionRange: hasManifestBlade ? { min: rankData.minDamage, max: rankData.maxDamage } : undefined,
        thornsRange: hasBristleSelfEffect ? { min: rankData.minDamage, max: rankData.maxDamage } : undefined,
        potency: rankData.effectPotency,
        dotDamage: rankData.dotDamage,
        regenAmount: rankData.regenPerTurn,
        chargesGranted: rankData.chargesGranted,
        summonHp: rankData.summonHp,
        summonDurationTurns: rankData.summonDurationTurns,
      }
    : undefined;
  const mechanicLines = formatAbilityMechanics(ability, configData, mechCtx);
  for (const line of mechanicLines) {
    lines.push(`<div class="tooltip-line tooltip-mechanic">${line}</div>`);
  }

  return lines;
}
